import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useCredits } from "../../context/CreditsContext";
import { ClipFields } from "./ClipFields";
import { ClipCard } from "./ClipCard";
import { PaidNote } from "./NewVideoForm";
import { EMPTY_CLIP, clipCost } from "../../hooks/useVideoStudio";

const AddClip = ({ video, pricing, onAdded, onClose }) => {
  const [clip, setClip] = useState(EMPTY_CLIP);
  const [busy, setBusy] = useState(false);
  const { refresh } = useCredits();
  const add = async () => {
    if (clip.prompt.trim().length < 3) return toast.error("Describe the clip in a few words");
    setBusy(true);
    try {
      onAdded(await api.addClip(video.id, { prompt: clip.prompt.trim(), photo_id: clip.photo?.id || null, quality: clip.quality, duration: clip.duration }));
      toast.success("Making your clip — usually 1–3 minutes");
      refresh();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="add-clip-form">
      <div className="flex items-center justify-between text-sm font-medium text-slate-900">Add a clip
        <button onClick={onClose} data-testid="add-clip-close" aria-label="Close" className="text-slate-400 hover:text-slate-700"><X size={15} /></button>
      </div>
      <ClipFields value={clip} setValue={setClip} pricing={pricing} idp="add-clip" />
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={add} disabled={busy} data-testid="add-clip-submit"
          className="h-9 px-4 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center gap-1.5 hover:bg-emerald-700 transition-colors disabled:opacity-60">
          {busy && <Loader2 size={13} className="animate-spin" />} Make clip · {clipCost(pricing, clip.quality, clip.duration)} credits
        </button>
        <PaidNote />
      </div>
    </div>
  );
};

export const ClipStrip = ({ video, pricing, playing, onPlay, onSave, onAdded }) => {
  const [adding, setAdding] = useState(!video.clips.length);
  const clips = video.clips;
  const edits = (list) => list.map((c) => ({ id: c.id, trim_start: c.trim_start || 0, trim_end: c.trim_end || 0 }));
  const move = (i, d) => { const l = [...clips]; [l[i], l[i + d]] = [l[i + d], l[i]]; onSave({ clips: edits(l) }); };
  const remove = (id) => onSave({ clips: edits(clips.filter((c) => c.id !== id)) });
  const trim = (id, s, e) => onSave({ clips: edits(clips.map((c) => (c.id === id ? { ...c, trim_start: s, trim_end: e } : c))) });

  return (
    <section className="space-y-3" data-testid="clip-strip">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Clips · played in this order</h3>
        {!adding && clips.length < 12 && (
          <button onClick={() => setAdding(true)} data-testid="add-clip-btn" className="h-8 px-3 rounded-full border border-slate-200 bg-white text-xs text-slate-700 flex items-center gap-1 hover:border-emerald-400 transition-colors"><Plus size={13} /> Add clip</button>
        )}
      </div>
      {clips.length > 0 && (
        <div className="flex gap-3 overflow-x-auto thin-scroll pb-1">
          {clips.map((c, i) => (
            <ClipCard key={c.id} clip={c} index={i} count={clips.length} active={playing === c.id} onPlay={() => onPlay(c.id)}
              onMove={(d) => move(i, d)} onRemove={() => remove(c.id)} onTrim={(s, e) => trim(c.id, s, e)} />
          ))}
        </div>
      )}
      {adding && <AddClip video={video} pricing={pricing} onAdded={onAdded} onClose={() => setAdding(false)} />}
    </section>
  );
};

export default ClipStrip;
